import { AnyObject } from '@store/types';
import { type Parser, parser as matchjsParser } from 'mathjs';
import compact from 'lodash/compact';

import { assertIsDefined } from '@utils/assertIsDefined';

import * as formulas from './formulas';
import { keysFrom } from './keysFrom';

export type Scope = Map<string, any>;

interface Params {
    scope: Scope;
    fields: FormulaParserField[];
    entryCreationTime: Date;
}

export interface FormulaParserField {
    name: string;
    formula?: string | null;
}

export interface ResultItem {
    name: string;
    formula: string;
    result: any;
}

export abstract class AbstractFormulaParser {
    private parser: Parser;

    private fields: FormulaParserField[];

    constructor({ fields, scope, entryCreationTime }: Params) {
        this.fields = fields;
        this.parser = this.createParser(this.mapScope(scope), entryCreationTime);
    }

    public compute(): ResultItem[] {
        // @ts-ignore
        return compact(
            this.getComputedFields().map(({ name, formula }) => {
                try {
                    assertIsDefined(formula, `Computed field ${name} must have a formula`);
                    const result = this.parser.evaluate(formula.replace(/ /g, ''));
                    return {
                        name,
                        formula,
                        result: this.formatResult(result),
                    };
                } catch (e) {
                    console.error('FormulaParser compute error:', e);
                    console.error('formula:', formula);

                    return undefined;
                }
            }),
        );
    }

    public calculate(expr: string): any {
        let result;
        try {
            result = this.parser.evaluate(expr.replace(/ /g, ''));
        } catch (e) {
            console.error('FormulaParser calculation error:', e);
            console.error('formula:', expr);
        }

        return this.formatResult(result);
    }

    public updateScope(scope: Scope): void {
        keysFrom(scope).forEach(name => {
            this.parser.set(this.fixName(name), scope.get(name));
        });
    }

    protected createParser(scope: Scope, entryCreationTime: Date): Parser {
        const parser = matchjsParser();

        for (const { name } of this.fields) {
            parser.set(this.fixName(name), scope.get(name));
        }

        parser.set('entryCreationTime', entryCreationTime);

        for (const key in formulas) {
            parser.set(key, (formulas as AnyObject)[key]);
        }

        return parser;
    }

    protected getComputedFields(): FormulaParserField[] {
        return this.fields.filter(item => item.formula);
    }

    protected fixName(name: string): string {
        return name.replace(/ /g, '');
    }

    protected formatResult(result: any): any {
        return result;
    }

    protected mapScope(scope: Scope): Scope {
        return scope;
    }
}
